import Phaser from 'phaser';

/**
 * 手続きで生成するテクスチャ。画像アセットを一切持たないので、起動時に描き起こす。
 *
 * 毎フレーム Graphics で描くには重すぎるもの（水中のコースティクス、周辺減光）と、
 * パーティクルに必要な素材だけをここで作る。生成は起動時の 1 回きり。
 */

export const TEX = {
  dot: 'etw-dot',
  spark: 'etw-spark',
  caustics: 'etw-caustics',
  vignette: 'etw-vignette',
} as const;

/** パーティクル用の丸。柔らかい縁を持たせて水しぶきに見えるようにする。 */
function makeDot(scene: Phaser.Scene): void {
  if (scene.textures.exists(TEX.dot)) return;
  const size = 32;
  const canvas = scene.textures.createCanvas(TEX.dot, size, size);
  if (!canvas) return;
  const ctx = canvas.getContext();
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.45, 'rgba(255,255,255,0.85)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  canvas.refresh();
}

/** 宝の取得やドルフィンスルー成功に使う、四方に伸びるきらめき。 */
function makeSpark(scene: Phaser.Scene): void {
  if (scene.textures.exists(TEX.spark)) return;
  const size = 48;
  const canvas = scene.textures.createCanvas(TEX.spark, size, size);
  if (!canvas) return;
  const ctx = canvas.getContext();
  const c = size / 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.95)';
  ctx.lineCap = 'round';
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI) / 2;
    const long = i % 2 === 0 ? c - 3 : c * 0.55;
    ctx.lineWidth = i % 2 === 0 ? 3 : 2;
    ctx.beginPath();
    ctx.moveTo(c, c);
    ctx.lineTo(c + Math.cos(angle) * long, c + Math.sin(angle) * long);
    ctx.stroke();
  }
  const glow = ctx.createRadialGradient(c, c, 0, c, c, c * 0.5);
  glow.addColorStop(0, 'rgba(255,255,255,0.9)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, size, size);
  canvas.refresh();
}

/**
 * 水面のコースティクス（光の網目）。
 * これがあるかないかで「塗られた面」か「水」かが決まる。
 * 周波数を 2π/size の整数倍にしてタイル化できるようにしてある。
 */
function makeCaustics(scene: Phaser.Scene): void {
  if (scene.textures.exists(TEX.caustics)) return;
  const size = 512;
  const canvas = scene.textures.createCanvas(TEX.caustics, size, size);
  if (!canvas) return;
  const ctx = canvas.getContext();
  const image = ctx.createImageData(size, size);
  const k = (2 * Math.PI) / size;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // 2 つの波の積がゼロになる曲線が網目になる。
      // 単純な正弦の重ね合わせだと斜めの筋になり、水ではなく引っかき傷に見える。
      // 周波数の違う 2 組を重ね、さらに低周波のむらを掛けて規則性を崩す。
      // 均一な格子のままだと、水面ではなく編み物に見えてしまう。
      const warp = 2.2;
      const a = Math.sin(k * 3 * x + Math.sin(k * 2 * y + 1.7) * warp);
      const b = Math.sin(k * 2 * y + Math.sin(k * 3 * x + 0.6) * warp);
      const c = Math.sin(k * 5 * x + Math.sin(k * 4 * y + 2.4) * 1.2);
      const d = Math.sin(k * 4 * y + Math.sin(k * 5 * x + 1.1) * 1.2);

      const coarse = Math.pow(Math.max(0, 1 - Math.abs(a * b)), 12);
      const fine = Math.pow(Math.max(0, 1 - Math.abs(c * d)), 18);
      const mask = 0.45 + 0.55 * Math.abs(Math.sin(k * x + 0.9) * Math.sin(k * y));

      const v = (coarse * 0.8 + fine * 0.45) * mask;
      const i = (y * size + x) * 4;
      image.data[i] = 255;
      image.data[i + 1] = 255;
      image.data[i + 2] = 240;
      image.data[i + 3] = Math.min(255, v * 255);
    }
  }
  ctx.putImageData(image, 0, 0);
  canvas.refresh();
}

/** 周辺減光。中央へ視線を集め、平坦な塗りに奥行きを与える。 */
function makeVignette(scene: Phaser.Scene): void {
  if (scene.textures.exists(TEX.vignette)) return;
  const w = 256;
  const h = 456;
  const canvas = scene.textures.createCanvas(TEX.vignette, w, h);
  if (!canvas) return;
  const ctx = canvas.getContext();
  const gradient = ctx.createRadialGradient(w / 2, h / 2, h * 0.28, w / 2, h / 2, h * 0.62);
  gradient.addColorStop(0, 'rgba(0,0,0,0)');
  gradient.addColorStop(1, 'rgba(0,0,0,1)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, w, h);
  canvas.refresh();
}

export function createGameTextures(scene: Phaser.Scene): void {
  makeDot(scene);
  makeSpark(scene);
  makeCaustics(scene);
  makeVignette(scene);
}
